/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * AGE GATE
 * ------------------------------------------------------------------------------------------------------------
 *
 * Drives the date-of-birth prompt rendered by snippets/age-gate.liquid on products tagged "age-restricted".
 *
 * The snippet renders the gate visible and hides it via its own inline script when a valid pass is already
 * stored, so the markup here is only ever reached by a visitor who still needs to verify. If JavaScript fails
 * to load the gate simply stays up, which is the safe direction to fail.
 *
 * This is a deterrent, not verification — see the note at the top of the snippet.
 */
(function () {
  var gate = document.getElementById('age-gate');

  if (!gate || gate.hidden) {
    return;
  }

  var STORAGE_KEY = 'eyland:age-verified';
  var form = document.getElementById('age-gate-form');
  var errorEl = document.getElementById('age-gate-error');
  var deniedEl = document.getElementById('age-gate-denied');
  var legalAge = parseInt(gate.dataset.legalAge, 10) || 21;
  var rememberDays = parseInt(gate.dataset.rememberDays, 10) || 30;

  /**
   * Whole years elapsed between two dates, rolling back the year when the birthday has not yet
   * come around. Avoids the 365.25-days-per-year approximation, which is off by a day near leap years.
   */
  function yearsBetween(birth, now) {
    var age = now.getFullYear() - birth.getFullYear();
    var monthDelta = now.getMonth() - birth.getMonth();

    if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  }

  /**
   * Returns a Date only if the three parts describe a real calendar date. Rejects rollovers such as
   * 31 February, which the Date constructor would otherwise silently turn into 2 or 3 March.
   */
  function toDate(month, day, year) {
    if (!month || !day || !year) {
      return null;
    }

    var date = new Date(year, month - 1, day);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  function showError(message) {
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function unlock() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ expires: Date.now() + rememberDays * 24 * 60 * 60 * 1000 })
      );
    } catch (e) {
      /* Private mode — the visitor passes for this page view but will be asked again. */
    }

    gate.hidden = true;
    document.documentElement.classList.remove('age-gate-open');
  }

  function deny() {
    form.hidden = true;
    deniedEl.hidden = false;
    deniedEl.querySelector('a').focus();
  }

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    errorEl.hidden = true;

    var now = new Date();
    var month = parseInt(form.month.value, 10);
    var day = parseInt(form.day.value, 10);
    var year = parseInt(form.year.value, 10);
    var birth = toDate(month, day, year);

    if (!birth) {
      showError('Please enter a complete, valid date of birth.');
      return;
    }

    var age = yearsBetween(birth, now);

    if (age < 0 || age > 120) {
      showError('Please check the date you entered.');
      return;
    }

    if (age < legalAge) {
      deny();
      return;
    }

    unlock();
  });

  // Keep tabbing inside the dialog so the product page behind it stays unreachable.
  gate.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab') {
      return;
    }

    var focusable = Array.prototype.filter.call(
      gate.querySelectorAll('a[href], button, input'),
      function (el) {
        return !el.hidden && el.offsetParent !== null;
      }
    );

    if (!focusable.length) {
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.getElementById('age-gate-month').focus();
})();